import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-aff54d60"),n=n(),_popScopeId(),n)
const _hoisted_1 = { key: 0 }
const _hoisted_2 = { class: "column-container" }
const _hoisted_3 = { class: "column" }
const _hoisted_4 = { class: "column" }
const _hoisted_5 = { class: "column-container" }
const _hoisted_6 = { class: "column" }
const _hoisted_7 = { class: "column" }
const _hoisted_8 = { class: "column-container" }
const _hoisted_9 = { class: "column" }
const _hoisted_10 = { class: "column" }

import { ref, inject, computed, watch, PropType } from "vue";
import modalBase from "@/components/ui/modals/modal-base.vue";
import displayErrorMessage from "@/utils/ErrorHandling";
import { getErrorMessage } from "bright-logic-core/src/errors";
import { Contacts } from "acquaint-api/models/Contacts";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import editAddress from "@/components/ui/address/edit-address.vue";
import { Address } from "@/types/generic/Address";
import { ContactsValidation } from "acquaint-api/validation/ContactsValidation";
import { EditAddressValidation } from "@/types/validation/EditAddressValidation";
import { CountryCode } from "bright-logic-core/src/CountryCode";
import { ModalWidth } from "@/types/ui/modals/ModalWidth";
import acquaintTextbox from "@/components/ui/inputs/acquaint-textbox.vue";
import { DxToolbarItem } from "devextreme-vue/popup";
import switchToggle from "@/components/ui/toggles/switch-toggle.vue";
import { LookupTableSource } from "acquaint-api/constants/LookupTableSource";
import lookupSingleSelect from "@/components/ui/lookup/lookup-single-select.vue";
import { FieldSize } from "@/enums/system/FieldSize";
import AcquaintDateBox from "@/components/ui/inputs/acquaint-date-box.vue";
import { DxTabPanel, DxItem } from "devextreme-vue/tab-panel";
import { HomeBusinessAddress } from "@/enums/HomeBusinessAddress";
import TabPanel from "@/components/ui/containers/tab-panel.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'edit-contact-details',
  props: {
  /**
   * The boolean value used with `v-model` to control the visibility or state of a modal or component.
   * @type {boolean}
   * @required
   */
  modelValue: {
    type: Boolean,
    required: true,
  },
  /**
   * The contact object containing details about the contact.
   * @type {Contacts}
   */
  contact: {
    type: Object as PropType<Contacts>,
  },
  /**
   * Determines whether to show the address by default.
   * @type {boolean}
   * @default true
   */
  showAddressField: {
    type: Boolean,
    default: true,
  },
},
  emits: [
  /**
   * Triggered when the `modelValue` changes. This is used to update the v-model variable in the parent component.
   * @property {boolean} value - Indicates whether the modal has been opened or closed.
   */
  "update:modelValue",
  /**
   * Emitted when the modal is closed.
   * Notifies the parent component whether data should be updated.
   */
  "modalClosed",
],
  setup(__props, { emit }) {

const props = __props;

const apiClient = inject("AcquaintApiClient") as AcquaintApiClient;
const originalContactData = ref<Contacts>();
const contactData = ref<Contacts>();
const homeMailingAddress = ref(0);
const businessMailingAddress = ref(0);
const saved = ref(false);
const maxBirthDate = ref(new Date(Date.now()));
const showCloseConfirmationPrompt = ref(false);
const keepingModalOpening = ref(false);
const selectedTab = ref(0);





const showContactModal = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit("update:modelValue", value),
});

const getContactData = async () => {
  try {
    originalContactData.value = await apiClient.httpGet(
      `database/contacts/${props.contact?.id}`,
    );
  } catch (ex) {
    displayErrorMessage(getErrorMessage(ex));
    originalContactData.value = undefined;
  }
};

const submitContactData = async () => {
  try {
    await apiClient.httpPut(
      `database/contacts/${props.contact?.id}`,
      contactData.value,
    );
    saved.value = true;
    return true;
  } catch (ex) {
    displayErrorMessage(getErrorMessage(ex));
    showCloseConfirmationPrompt.value = true;
    return false;
  }
};

const homeAddress = ref<Address>({
  countryCode: CountryCode.UK,
  postCode: "",
  propertyName: "",
  street: "",
  locality: "",
  town: "",
  region: "",
  country: "",
});

const workAddress = ref<Address>({
  countryCode: CountryCode.UK,
  postCode: "",
  propertyName: "",
  street: "",
  locality: "",
  town: "",
  region: "",
  country: "",
});

//When a home or work address is selected or a postcode is simply entered for either, the contact data is updated to reflect the data that has been entered.

watch(
  () => homeAddress.value,
  () => {
    if (!contactData.value || !homeAddress.value) {
      return;
    }

    contactData.value.homeProperty = homeAddress.value.propertyName;
    contactData.value.homeStreet = homeAddress.value.street;
    contactData.value.homeLocality = homeAddress.value.locality;
    contactData.value.homeTown = homeAddress.value.town;
    contactData.value.homeRegion = homeAddress.value.region;
    contactData.value.homePostcode = homeAddress.value.postCode;
    contactData.value.homeCountryCode = homeAddress.value.countryCode;
  },
  { deep: true },
);

watch(
  () => workAddress.value,
  () => {
    if (!contactData.value || !workAddress.value) {
      return;
    }

    contactData.value.businessProperty = workAddress.value.propertyName;
    contactData.value.businessStreet = workAddress.value.street;
    contactData.value.businessLocality = workAddress.value.locality;
    contactData.value.businessTown = workAddress.value.town;
    contactData.value.businessRegion = workAddress.value.region;
    contactData.value.businessPostcode = workAddress.value.postCode;
    contactData.value.businessCountryCode = workAddress.value.countryCode;
  },
  { deep: true },
);

watch(
  () => props.modelValue,
  async () => {
    if (!props.modelValue || keepingModalOpening.value) {
      return;
    }

    selectedTab.value = 0;
    await getContactData();

    if (!originalContactData.value) {
      contactData.value = undefined;
      return;
    }

    //When the contact data has beeen retrieved from the API, the address details for the edit adress components must be updated.

    homeAddress.value.countryCode = originalContactData.value.homeCountryCode;
    homeAddress.value.postCode = originalContactData.value.homePostcode;
    homeAddress.value.propertyName = originalContactData.value.homeProperty;
    homeAddress.value.street = originalContactData.value.homeStreet;
    homeAddress.value.locality = originalContactData.value.homeLocality;
    homeAddress.value.town = originalContactData.value.homeTown;
    homeAddress.value.region = originalContactData.value.homeRegion;

    workAddress.value.countryCode =
      originalContactData.value.businessCountryCode;
    workAddress.value.postCode = originalContactData.value.businessPostcode;
    workAddress.value.propertyName = originalContactData.value.businessProperty;
    workAddress.value.street = originalContactData.value.businessStreet;
    workAddress.value.locality = originalContactData.value.businessLocality;
    workAddress.value.town = originalContactData.value.businessTown;
    workAddress.value.region = originalContactData.value.businessRegion;

    contactData.value = JSON.parse(JSON.stringify(originalContactData.value));

    saved.value = false;

    if (contactData.value?.mailingAddress) {
      businessMailingAddress.value = 1;
      homeMailingAddress.value = 0;
    } else if (contactData.value?.mailingAddress == 0) {
      homeMailingAddress.value = 1;
      businessMailingAddress.value = 0;
    }
  },
);

const validation = ref<EditAddressValidation>({
  propertyName: ContactsValidation.HomeProperty,
  street: ContactsValidation.HomeStreet,
  locality: ContactsValidation.HomeLocality,
  town: ContactsValidation.HomeTown,
  region: ContactsValidation.HomeRegion,
  postcode: ContactsValidation.HomePostcode,
});

const saving = ref(false);

const submitChanges = async (e: any) => {
  saving.value = true;
  const validate = e.validationGroup.validate();

  if (e.validationGroup._validationInfo.result && validate.isValid) {
    if (await submitContactData()) {
      emit("modalClosed", true, contactData.value);
      showCloseConfirmationPrompt.value = false;
      showContactModal.value = false;
    }
  }
  saving.value = false;
};

const cancel = () => {
  showContactModal.value = false;
};

const modalClosed = async () => {
  emit(
    "modalClosed",
    JSON.stringify(originalContactData.value) != JSON.stringify(props.contact),
  );
  keepingModalOpening.value = false;
  showCloseConfirmationPrompt.value = false;
};

//If the mailing address toggle has been toggled on in the home address tab, the mailing address toggle has been toggled on in the work address tab is toggled off
//The businessMailingAddress.value is either 0 or 1 depending which toggle is set to being on

watch(
  () => homeMailingAddress.value,
  () => {
    if (!contactData.value) {
      return;
    }

    showCloseConfirmationPrompt.value =
      !saved.value &&
      JSON.stringify(originalContactData.value) !=
        JSON.stringify(contactData.value);

    if (homeMailingAddress.value == 0) {
      businessMailingAddress.value = 1;
    } else if (homeMailingAddress.value == 1) {
      contactData.value.mailingAddress = HomeBusinessAddress.homeMailingAddress;
      businessMailingAddress.value = 0;
    }
  },
);

watch(
  () => homeMailingAddress.value,
  () => {
    showCloseConfirmationPrompt.value =
      !saved.value &&
      JSON.stringify(originalContactData.value) !=
        JSON.stringify(contactData.value);
  },
);

watch(
  () => businessMailingAddress.value,
  () => {
    showCloseConfirmationPrompt.value =
      !saved.value &&
      JSON.stringify(originalContactData.value) !=
        JSON.stringify(contactData.value);
  },
  { deep: true },
);

watch(
  () => businessMailingAddress.value,
  () => {
    if (!contactData.value) {
      return;
    }

    showCloseConfirmationPrompt.value =
      !saved.value &&
      JSON.stringify(originalContactData.value) !=
        JSON.stringify(contactData.value);

    if (businessMailingAddress.value == 0) {
      homeMailingAddress.value = 1;
    } else if (businessMailingAddress.value == 1) {
      contactData.value.mailingAddress =
        HomeBusinessAddress.businessMailingAddress;
      homeMailingAddress.value = 0;
    }
  },
  { deep: true },
);

watch(
  () => contactData.value,
  () => {
    showCloseConfirmationPrompt.value =
      JSON.stringify(originalContactData.value) !=
      JSON.stringify(contactData.value);
  },
  { deep: true },
);

const emailFieldUnfocused = () => {
  if (
    contactData.value &&
    contactData.value.emailAlt != "" &&
    contactData.value.email == ""
  ) {
    contactData.value.email = contactData.value.emailAlt;
    contactData.value.emailAlt = "";
  }
};

const fieldSize = computed(() => {
  return FieldSize.Full;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(modalBase, {
    modelValue: showContactModal.value,
    "onUpdate:modelValue": _cache[22] || (_cache[22] = ($event: any) => ((showContactModal).value = $event)),
    title: "Personal Information",
    width: _unref(ModalWidth).Large,
    height: "72vh",
    saving: saving.value,
    showCloseConfirmationPrompt: showCloseConfirmationPrompt.value,
    onCancelPromptDenied: _cache[23] || (_cache[23] = ($event: any) => (keepingModalOpening.value = true)),
    onModalClosed: modalClosed,
    showCloseButton: true
  }, {
    content: _withCtx(() => [
      (contactData.value && homeAddress.value && workAddress.value)
        ? (_openBlock(), _createElementBlock("section", _hoisted_1, [
            (contactData.value)
              ? (_openBlock(), _createBlock(_unref(DxTabPanel), {
                  key: 0,
                  selectedIndex: selectedTab.value,
                  "onUpdate:selectedIndex": _cache[21] || (_cache[21] = ($event: any) => ((selectedTab).value = $event)),
                  "scroll-by-content": true,
                  "show-nav-buttons": true,
                  "swipe-enabled": false
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(DxItem), { title: "Main Details" }, {
                      default: _withCtx(() => [
                        _createVNode(TabPanel, null, {
                          default: _withCtx(() => [
                            _createElementVNode("section", _hoisted_2, [
                              _createElementVNode("div", _hoisted_3, [
                                _createVNode(acquaintTextbox, {
                                  modelValue: contactData.value.lastName,
                                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((contactData.value.lastName) = $event)),
                                  label: "Last Name",
                                  fieldValidation: _unref(ContactsValidation).LastName,
                                  width: fieldSize.value,
                                  validationGroup: "editContactDetails"
                                }, null, 8, ["modelValue", "fieldValidation", "width"]),
                                _createVNode(acquaintTextbox, {
                                  modelValue: contactData.value.firstNames,
                                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((contactData.value.firstNames) = $event)),
                                  label: "First Names",
                                  fieldValidation: _unref(ContactsValidation).LastName,
                                  width: fieldSize.value,
                                  validationGroup: "editContactDetails"
                                }, null, 8, ["modelValue", "fieldValidation", "width"]),
                                _createVNode(lookupSingleSelect, {
                                  modelValue: contactData.value.titleID,
                                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((contactData.value.titleID) = $event)),
                                  "lookup-source": _unref(LookupTableSource).Titles,
                                  label: "Title",
                                  width: fieldSize.value,
                                  validationGroup: "editContactDetails"
                                }, null, 8, ["modelValue", "lookup-source", "width"]),
                                _createVNode(acquaintTextbox, {
                                  modelValue: contactData.value.salutation,
                                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((contactData.value.salutation) = $event)),
                                  label: "Salutation",
                                  fieldValidation: _unref(ContactsValidation).Salutation,
                                  width: fieldSize.value,
                                  validationGroup: "editContactDetails"
                                }, null, 8, ["modelValue", "fieldValidation", "width"]),
                                _createVNode(lookupSingleSelect, {
                                  modelValue: contactData.value.sourceID,
                                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((contactData.value.sourceID) = $event)),
                                  "lookup-source": _unref(LookupTableSource).Sources,
                                  label: "Source",
                                  width: fieldSize.value
                                }, null, 8, ["modelValue", "lookup-source", "width"]),
                                _createVNode(AcquaintDateBox, {
                                  modelValue: contactData.value.birthDate,
                                  "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((contactData.value.birthDate) = $event)),
                                  validationGroup: "editContactDetails",
                                  "field-validation": _unref(ContactsValidation).BirthDate,
                                  width: _unref(FieldSize).Medium,
                                  label: "Birth Date",
                                  "max-date": maxBirthDate.value
                                }, null, 8, ["modelValue", "field-validation", "width", "max-date"])
                              ]),
                              _createElementVNode("div", _hoisted_4, [
                                _createVNode(acquaintTextbox, {
                                  modelValue: contactData.value.mobileTelephone,
                                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((contactData.value.mobileTelephone) = $event)),
                                  label: "Mobile",
                                  fieldValidation: _unref(ContactsValidation).MobileTelephone,
                                  width: fieldSize.value,
                                  validationGroup: "editContactDetails"
                                }, null, 8, ["modelValue", "fieldValidation", "width"]),
                                _createVNode(acquaintTextbox, {
                                  modelValue: contactData.value.email,
                                  "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((contactData.value.email) = $event)),
                                  label: "Email #1",
                                  onOnUnfocused: emailFieldUnfocused,
                                  fieldValidation: _unref(ContactsValidation).Email,
                                  width: fieldSize.value,
                                  validationGroup: "editContactDetails"
                                }, null, 8, ["modelValue", "fieldValidation", "width"]),
                                _createVNode(acquaintTextbox, {
                                  modelValue: contactData.value.emailAlt,
                                  "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((contactData.value.emailAlt) = $event)),
                                  label: "Email #2",
                                  onOnUnfocused: emailFieldUnfocused,
                                  fieldValidation: _unref(ContactsValidation).EmailAlt,
                                  width: fieldSize.value,
                                  validationGroup: "editContactDetails"
                                }, null, 8, ["modelValue", "fieldValidation", "width"]),
                                _createVNode(acquaintTextbox, {
                                  modelValue: contactData.value.webPage,
                                  "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((contactData.value.webPage) = $event)),
                                  label: "Web Page",
                                  fieldValidation: _unref(ContactsValidation).WebPage,
                                  validationGroup: "editContactDetails",
                                  width: fieldSize.value
                                }, null, 8, ["modelValue", "fieldValidation", "width"]),
                                _createVNode(acquaintTextbox, {
                                  modelValue: contactData.value.businessTelephone,
                                  "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((contactData.value.businessTelephone) = $event)),
                                  label: "Business Telephone",
                                  fieldValidation: _unref(ContactsValidation).BusinessTelephone,
                                  width: fieldSize.value,
                                  validationGroup: "editContactDetails"
                                }, null, 8, ["modelValue", "fieldValidation", "width"])
                              ])
                            ])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(DxItem), {
                      title: "Home Address",
                      visible: props.showAddressField
                    }, {
                      default: _withCtx(() => [
                        _createVNode(TabPanel, null, {
                          default: _withCtx(() => [
                            _createElementVNode("section", _hoisted_5, [
                              _createElementVNode("div", _hoisted_6, [
                                _createVNode(editAddress, {
                                  modelValue: homeAddress.value,
                                  "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((homeAddress).value = $event)),
                                  validation: validation.value,
                                  width: fieldSize.value,
                                  validationGroup: "editContactDetails"
                                }, null, 8, ["modelValue", "validation", "width"]),
                                _createVNode(switchToggle, {
                                  modelValue: homeMailingAddress.value,
                                  "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((homeMailingAddress).value = $event)),
                                  label: "Mailing Address",
                                  readOnly: false
                                }, null, 8, ["modelValue"])
                              ]),
                              _createElementVNode("div", _hoisted_7, [
                                _createVNode(acquaintTextbox, {
                                  modelValue: contactData.value.homeTelephone,
                                  "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((contactData.value.homeTelephone) = $event)),
                                  label: "Home Telephone",
                                  fieldValidation: _unref(ContactsValidation).HomeTelephone,
                                  validationGroup: "editContactDetails",
                                  width: fieldSize.value
                                }, null, 8, ["modelValue", "fieldValidation", "width"]),
                                _createVNode(acquaintTextbox, {
                                  modelValue: contactData.value.homeTelephoneAlt,
                                  "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((contactData.value.homeTelephoneAlt) = $event)),
                                  label: "Alt Telephone",
                                  fieldValidation: _unref(ContactsValidation).HomeTelephoneAlt,
                                  validationGroup: "editContactDetails",
                                  width: fieldSize.value
                                }, null, 8, ["modelValue", "fieldValidation", "width"])
                              ])
                            ])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }, 8, ["visible"]),
                    _createVNode(_unref(DxItem), {
                      title: "Business Address",
                      visible: props.showAddressField
                    }, {
                      default: _withCtx(() => [
                        _createVNode(TabPanel, null, {
                          default: _withCtx(() => [
                            _createElementVNode("section", _hoisted_8, [
                              _createElementVNode("div", _hoisted_9, [
                                _createVNode(editAddress, {
                                  modelValue: workAddress.value,
                                  "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((workAddress).value = $event)),
                                  validation: validation.value,
                                  width: fieldSize.value,
                                  validationGroup: "editContactDetails"
                                }, null, 8, ["modelValue", "validation", "width"]),
                                _createVNode(switchToggle, {
                                  modelValue: businessMailingAddress.value,
                                  "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((businessMailingAddress).value = $event)),
                                  label: "Mailing Address",
                                  readOnly: false
                                }, null, 8, ["modelValue"])
                              ]),
                              _createElementVNode("div", _hoisted_10, [
                                _createVNode(acquaintTextbox, {
                                  modelValue: contactData.value.businessCompany,
                                  "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((contactData.value.businessCompany) = $event)),
                                  label: "Company Name",
                                  fieldValidation: _unref(ContactsValidation).BusinessCompany,
                                  width: fieldSize.value,
                                  validationGroup: "editContactDetails"
                                }, null, 8, ["modelValue", "fieldValidation", "width"]),
                                _createVNode(acquaintTextbox, {
                                  modelValue: contactData.value.businessDepartment,
                                  "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event: any) => ((contactData.value.businessDepartment) = $event)),
                                  label: "Department",
                                  fieldValidation: _unref(ContactsValidation).BusinessDepartment,
                                  width: fieldSize.value,
                                  validationGroup: "editContactDetails"
                                }, null, 8, ["modelValue", "fieldValidation", "width"]),
                                _createVNode(acquaintTextbox, {
                                  modelValue: contactData.value.businessJobTitle,
                                  "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event: any) => ((contactData.value.businessJobTitle) = $event)),
                                  label: "Job Title",
                                  fieldValidation: _unref(ContactsValidation).BusinessJobTitle,
                                  width: fieldSize.value,
                                  validationGroup: "editContactDetails"
                                }, null, 8, ["modelValue", "fieldValidation", "width"]),
                                _createVNode(acquaintTextbox, {
                                  modelValue: contactData.value.businessQualifications,
                                  "onUpdate:modelValue": _cache[20] || (_cache[20] = ($event: any) => ((contactData.value.businessQualifications) = $event)),
                                  label: "Qualifications",
                                  fieldValidation: _unref(ContactsValidation).BusinessQualifications,
                                  width: fieldSize.value,
                                  validationGroup: "editContactDetails"
                                }, null, 8, ["modelValue", "fieldValidation", "width"])
                              ])
                            ])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }, 8, ["visible"])
                  ]),
                  _: 1
                }, 8, ["selectedIndex"]))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true)
    ]),
    footer: _withCtx(() => [
      _createVNode(_unref(DxToolbarItem), {
        widget: "dxButton",
        toolbar: "bottom",
        location: "after",
        options: {
          text: 'Save',
          disabled: saving.value,
          onClick: (e) => {
            submitChanges(e);
          },
          validationGroup: 'editContactDetails',
        }
      }, null, 8, ["options"]),
      _createVNode(_unref(DxToolbarItem), {
        widget: "dxButton",
        toolbar: "bottom",
        location: "after",
        options: {
          text: 'Cancel',
          disabled: saving.value,
          onClick: () => {
            cancel();
          },
        }
      }, null, 8, ["options"])
    ]),
    _: 1
  }, 8, ["modelValue", "width", "saving", "showCloseConfirmationPrompt"]))
}
}

})